// Gère la modale « Télécharger la documentation » : soumission AJAX du formulaire,
// puis bascule vers la modale de confirmation (téléchargement direct ou envoi par email).
// Monté sur la racine de la modale via is="documentation-download".
export class DocumentationDownload extends HTMLDivElement {

    private form: HTMLFormElement | null = null;
    private siteInput: HTMLInputElement | null = null;
    private btnSubmit: HTMLButtonElement | null = null;
    private errorBox: HTMLElement | null = null;
    private confirmModal: HTMLElement | null = null;
    private btnDownloadConfirm: HTMLAnchorElement | null = null;
    private submitUrl: string = '';
    private recaptchaSiteKey: string = '';

    connectedCallback() {
        this.submitUrl = this.getAttribute('data-submit-url') ?? '';
        this.recaptchaSiteKey = this.getAttribute('data-recaptcha-site-key') ?? '';

        // Éléments internes à la modale (this === #modalTelechargerDocumentation).
        this.form      = this.querySelector<HTMLFormElement>('#formDocumentation');
        this.siteInput = this.querySelector<HTMLInputElement>('#docFormationSiteId');
        this.btnSubmit = this.querySelector<HTMLButtonElement>('#btnDocSubmit');
        this.errorBox  = this.querySelector<HTMLElement>('#docFormError');

        // Modale de confirmation (en dehors de this).
        this.confirmModal       = document.querySelector<HTMLElement>('#modalTelechargerDocumentationConfirmation');
        this.btnDownloadConfirm = this.confirmModal?.querySelector<HTMLAnchorElement>('#btnDocDownloadConfirm') ?? null;

        if (!this.form) return;

        // Les CTA « Télécharger » des cartes session renseignent le site ciblé.
        document.querySelectorAll<HTMLElement>('.download-cta').forEach(btn => {
            btn.addEventListener('click', this.onDownloadCtaClick);
        });

        this.btnDownloadConfirm?.addEventListener('click', this.onConfirmDownloadClick);
        this.form.addEventListener('submit', this.onSubmit);
    }

    // Renseigne le jeton reCAPTCHA v3 attendu par le serveur avant l'envoi AJAX.
    private async fillRecaptchaToken(): Promise<void> {
        const input = this.querySelector<HTMLInputElement>('#docRecaptchaResponse');
        const grecaptcha = (window as any).grecaptcha;

        if (!input || !this.recaptchaSiteKey || !grecaptcha) return;

        try {
            input.value = await new Promise<string>((resolve, reject) => {
                grecaptcha.ready(() => {
                    grecaptcha
                        .execute(this.recaptchaSiteKey, { action: 'documentation' })
                        .then(resolve, reject);
                });
            });
        } catch {
            // Jeton indisponible : le serveur refusera la demande et le message d'erreur s'affichera.
            input.value = '';
        }
    }

    private onDownloadCtaClick = (event: Event) => {
        const btn = event.currentTarget as HTMLElement;
        if (this.siteInput) this.siteInput.value = btn.dataset.formationSiteId ?? '';
    };

    private onConfirmDownloadClick = () => {
        const btn = this.btnDownloadConfirm;
        if (!btn || btn.getAttribute('href') === '#') return;

        btn.querySelector('.btn-icon')?.classList.add('d-none');
        btn.querySelector('.btn-spinner')?.classList.remove('d-none');
        btn.classList.add('disabled');

        setTimeout(() => {
            btn.querySelector('.btn-icon')?.classList.remove('d-none');
            btn.querySelector('.btn-spinner')?.classList.add('d-none');
            btn.classList.remove('disabled');
        }, 3000);
    };

    private onSubmit = async (e: Event) => {
        e.preventDefault();

        const form = this.form;
        if (!form) return;

        if (!form.checkValidity()) {
            form.classList.add('was-validated');
            return;
        }

        const btnText    = this.btnSubmit?.querySelector('.btn-text');
        const btnSpinner = this.btnSubmit?.querySelector('.btn-spinner');

        btnText?.classList.add('d-none');
        btnSpinner?.classList.remove('d-none');
        if (this.btnSubmit) this.btnSubmit.disabled = true;
        this.errorBox?.classList.add('d-none');

        try {
            await this.fillRecaptchaToken();

            const resp = await fetch(this.submitUrl, { method: 'POST', body: new FormData(form) });
            const json = await resp.json();

            if (!resp.ok || !json.success) {
                if (this.errorBox) {
                    this.errorBox.textContent = json.error || 'Une erreur est survenue. Veuillez réessayer.';
                    this.errorBox.classList.remove('d-none');
                }
                return;
            }

            const bootstrap = (window as any).bootstrap;
            bootstrap?.Modal.getInstance(this)?.hide();

            // Réinitialise la modale de confirmation à son état par défaut avant affichage.
            const confirmMessage = this.confirmModal?.querySelector('p.text-secondary');
            this.btnDownloadConfirm?.classList.remove('d-none');
            if (this.btnDownloadConfirm) this.btnDownloadConfirm.href = '#';
            if (confirmMessage) confirmMessage.textContent = 'Cliquez sur le bouton ci-dessous pour télécharger le document.';

            if (json.action === 'download' && json.downloadUrl) {
                if (this.btnDownloadConfirm) this.btnDownloadConfirm.href = json.downloadUrl;
            } else {
                this.btnDownloadConfirm?.classList.add('d-none');
                if (confirmMessage) confirmMessage.textContent = 'La documentation vous a été envoyée par email.';
            }

            if (this.confirmModal) new bootstrap.Modal(this.confirmModal).show();

            form.reset();
            form.classList.remove('was-validated');
        } finally {
            btnText?.classList.remove('d-none');
            btnSpinner?.classList.add('d-none');
            if (this.btnSubmit) this.btnSubmit.disabled = false;
        }
    };
}
